﻿import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInView } from '../hooks/useInView';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';

// ── Color tokens ──────────────────────────────────────────────────────────────
const C = {
  bg: '#0B1628', surface: '#0D1E38', navy: '#1B3A6B',
  gold: '#D4A017', goldLight: '#F0C040', green: '#1A7A4A',
  muted: 'rgba(255,255,255,0.55)', border: 'rgba(255,255,255,0.07)',
};

// ── Data ─────────────────────────────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'grocery', tab: '🛒 Grocery', name: 'Sunrise General Store',
    revenue: 18400, cogs: 11040, gross: 7360, rent: 1800, salaries: 1280, net: 4280,
    products: 142, customers: 284, revTrend: '+12%', netTrend: '+23%',
    sales: [{ item: 'Rice 25kg', amt: 450 }, { item: 'Cooking Oil ×12', amt: 280 }, { item: 'Sugar 5kg', amt: 85 }],
  },
  {
    id: 'pharmacy', tab: '💊 Pharmacy', name: "Ama's Pharmacy",
    revenue: 34800, cogs: 20880, gross: 13920, rent: 3200, salaries: 4100, net: 6620,
    products: 380, customers: 512, revTrend: '+18%', netTrend: '+31%',
    sales: [{ item: 'Panadol (bulk)', amt: 1200 }, { item: 'Antibiotics', amt: 680 }, { item: 'Vitamins', amt: 340 }],
  },
  {
    id: 'fashion', tab: '👗 Fashion', name: 'Akosua Fashion House',
    revenue: 22600, cogs: 11300, gross: 11300, rent: 2400, salaries: 2000, net: 6900,
    products: 89, customers: 198, revTrend: '+34%', netTrend: '+44%',
    sales: [{ item: 'Kente Dress', amt: 890 }, { item: 'Ankara Top', amt: 320 }, { item: 'Accessories', amt: 180 }],
  },
  {
    id: 'electronics', tab: '📱 Electronics', name: 'Tech Hub',
    revenue: 58200, cogs: 40740, gross: 17460, rent: 4500, salaries: 3800, net: 9160,
    products: 234, customers: 389, revTrend: '+8%', netTrend: '+15%',
    sales: [{ item: 'Samsung A54', amt: 2100 }, { item: 'Earbuds ×2', amt: 480 }, { item: 'Phone Case', amt: 55 }],
  },
  {
    id: 'restaurant', tab: '🍽 Restaurant', name: "Mama's Kitchen",
    revenue: 14200, cogs: 7100, gross: 7100, rent: 1600, salaries: 2400, net: 3100,
    products: 48, customers: 620, revTrend: '+21%', netTrend: '+19%',
    sales: [{ item: 'Fufu & Soup ×8', amt: 360 }, { item: 'Jollof Rice ×5', amt: 225 }, { item: 'Drinks ×12', amt: 180 }],
  },
];

const FEATURES = [
  { icon: '📦', title: 'Smart Inventory',     desc: 'Track every product. Get alerts when stock runs low. Never tell a customer "we are out" again.' },
  { icon: '💰', title: 'Real Profit Tracking', desc: 'See your actual profit after product costs AND operating expenses. Not just revenue.' },
  { icon: '👥', title: 'Customer Records',    desc: 'Know who owes you, who buys the most, and send invoices directly from the app.' },
  { icon: '📄', title: 'PDF Invoices',        desc: 'Generate professional invoices instantly. Send by email or WhatsApp in seconds.' },
  { icon: '📱', title: 'Works on Mobile',     desc: 'Install on your phone like an app. Works even when internet is slow or off.' },
  { icon: '📊', title: 'Business Dashboard',  desc: 'See your best products, busiest days, and profit trends at a glance every morning.' },
];

const TESTIMONIALS = [
  {
    quote: "I used to guess my profit at end of day. Now I know exactly where every cedi goes. My husband couldn't believe how much more we're making now that I track properly.",
    name: 'Abena Mensah', role: 'Clothing Store, Kumasi', color: C.gold, initials: 'AM',
  },
  {
    quote: "I found out my most popular product was actually losing me money because I wasn't counting delivery cost. OptiKa showed me in seconds. I adjusted my price and now it's my best seller.",
    name: 'Kweku Owusu', role: 'Electronics Shop, Accra', color: C.green, initials: 'KO',
  },
  {
    quote: "The invoice feature saved me so much time and my customers trust me more because everything looks professional. I send invoices on WhatsApp directly from the app.",
    name: 'Ama Darko', role: 'Pharmacy, Takoradi', color: '#2E5FA3', initials: 'AD',
  },
];

const CHAT = [
  { type: 'user',    av: 'AK', color: C.gold,    name: 'Aku K.',     time: '9:14 AM',  text: "I just started using OptiKa for my pharmacy. How do I track which medicines are about to expire?" },
  { type: 'support', av: 'BT', color: C.navy,    name: 'OptiKa',   time: '9:15 AM',  text: "Hi Aku! When adding inventory, set the expiry date field and OptiKa will alert you 30 days before it expires. You can filter by expiry in your inventory view 🎉" },
  { type: 'user',    av: 'KB', color: C.green,   name: 'Kwabena B.', time: '2:31 PM',  text: "My revenue was GHS12,000 last month but I still had no money at the end. OptiKa helped me see I was spending GHS9,500 on restocking without realising it 😮" },
  { type: 'user',    av: 'EO', color: '#2E5FA3', name: 'Efua O.',    time: '11:02 AM', text: "This app is exactly what I needed. I used to write everything in a notebook and always lose track of who owes me. The customer debt feature is 🔥" },
  { type: 'support', av: 'BT', color: C.navy,    name: 'OptiKa',   time: '11:04 AM', text: "Thank you Efua! The debt tracker automatically sends reminders to customers who owe you. You can also see a full breakdown of who owes how much and since when 💪" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtC = (n) => `GHS ${Math.round(n).toLocaleString()}`;
const fmtK = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

// ── Reusable: Gold button ─────────────────────────────────────────────────────
function GoldBtn({ to, onClick, children, big, style = {} }) {
  const base = {
    display: 'inline-block', textDecoration: 'none', cursor: 'pointer',
    background: C.gold, color: '#0F172A', border: 'none',
    padding: big ? '16px 40px' : '10px 22px',
    borderRadius: big ? 14 : 11,
    fontFamily: 'Sora, sans-serif', fontWeight: 800,
    fontSize: big ? '1.05rem' : '0.9rem',
    boxShadow: '0 4px 24px rgba(212,160,23,0.35)',
    transition: 'all 300ms cubic-bezier(0.34,1.56,0.64,1)',
    ...style,
  };
  const enter = (e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)'; e.currentTarget.style.boxShadow = '0 8px 40px rgba(212,160,23,0.55)'; e.currentTarget.style.background = C.goldLight; };
  const leave = (e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(212,160,23,0.35)'; e.currentTarget.style.background = C.gold; };
  if (to) return <Link to={to} style={base} onMouseEnter={enter} onMouseLeave={leave}>{children}</Link>;
  return <button onClick={onClick} style={base} onMouseEnter={enter} onMouseLeave={leave}>{children}</button>;
}

// ── Reusable: P&L row ─────────────────────────────────────────────────────────
function PLRow({ label, value, color, minus, bold, large }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${large ? 12 : 8}px 0`, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ color: bold ? 'white' : 'rgba(255,255,255,0.6)', fontSize: large ? '0.95rem' : '0.875rem', fontWeight: bold ? 700 : 400 }}>
        {minus && <span style={{ color: '#EF4444', marginRight: 4 }}>−</span>}{label}
      </span>
      <span style={{
        color: color || 'white',
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontWeight: bold ? 800 : 600,
        fontSize: large ? '1.1rem' : '0.9rem',
        minWidth: '11ch',
        display: 'inline-block',
        textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  );
}

// ── Phone mockup ──────────────────────────────────────────────────────────────
function PhoneMockup({ si }) {
  const s = SCENARIOS[si];
  const chartPts = "0,32 14,27 28,29 42,19 56,22 70,14 84,17 98,9 112,13 126,7 140,11 154,5 160,8";

  return (
    <div style={{ position: 'relative', flexShrink: 0, width: 260 }}>
      {/* Phone frame */}
      <div style={{
        width: 220, height: 460, borderRadius: 36, marginLeft: 20,
        background: '#060E1A', border: '10px solid #1E3A6B',
        boxShadow: '0 0 0 1.5px rgba(212,160,23,0.2), 0 32px 80px rgba(0,0,0,0.7), inset 0 0 24px rgba(0,0,0,0.4)',
        position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Notch */}
        <div style={{ width: 72, height: 18, borderRadius: '0 0 14px 14px', background: '#1E3A6B', margin: '0 auto', flexShrink: 0 }} />

        {/* Screen */}
        <div style={{ flex: 1, padding: '6px 10px 0', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '6.5px', fontWeight: 700, color: 'rgba(255,255,255,0.85)', fontFamily: 'Sora, sans-serif', maxWidth: '75%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.name}
            </div>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: `linear-gradient(135deg,${C.gold},${C.goldLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5.5px', fontWeight: 800, color: '#0F172A', flexShrink: 0 }}>B</div>
          </div>

          {/* 4 mini KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {[
              { label: 'Revenue',    val: `GHS ${fmtK(s.revenue)}`,         color: '#22C55E' },
              { label: 'Net Profit', val: `GHS ${fmtK(s.net)}`,             color: C.gold },
              { label: 'Expenses',   val: `GHS ${fmtK(s.rent + s.salaries)}`, color: '#EF4444' },
              { label: 'Low Stock',  val: '4 items',                        color: '#F59E0B' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 7, padding: '5px 6px', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ fontSize: '5px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: '7.5px', fontWeight: 800, color, fontFamily: 'Sora, sans-serif' }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Mini chart */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 7, padding: '5px 7px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '5px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>30-Day Revenue</div>
            <svg viewBox="0 0 160 36" width="100%" height="26" style={{ display: 'block' }}>
              <defs>
                <linearGradient id="lpCG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.gold} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={C.gold} stopOpacity="0" />
                </linearGradient>
              </defs>
              <polygon points={`0,36 ${chartPts} 160,36`} fill="url(#lpCG)" />
              <polyline points={chartPts} stroke={C.gold} strokeWidth="1.5" fill="none" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Recent sales */}
          <div>
            <div style={{ fontSize: '5px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>Recent Sales</div>
            {s.sales.map((sale, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                <span style={{ fontSize: '6px', color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '68%' }}>{sale.item}</span>
                <span style={{ fontSize: '6px', fontWeight: 700, color: '#22C55E', flexShrink: 0 }}>GHS{sale.amt}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom nav */}
        <div style={{ display: 'flex', justifyContent: 'space-around', padding: '7px 10px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.25)', flexShrink: 0 }}>
          {['⊞', '📦', '🛒', '💰'].map((icon, i) => (
            <div key={i} style={{ fontSize: i === 0 ? '10px' : '8px', opacity: i === 0 ? 1 : 0.4, background: i === 0 ? 'rgba(212,160,23,0.14)' : 'transparent', borderRadius: 5, padding: '2px 7px' }}>
              {icon}
            </div>
          ))}
        </div>
      </div>

      {/* Floating notification — Low stock (top-right) */}
      <div style={{
        position: 'absolute', top: 55, right: -148,
        width: 178, background: 'rgba(10,20,44,0.97)',
        border: `1px solid rgba(212,160,23,0.45)`, borderRadius: 12, padding: '9px 11px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        animation: 'float-in-right 600ms 900ms ease both, float-bob 3s 1.6s ease-in-out infinite',
        zIndex: 2,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>⚠</div>
          <div>
            <div style={{ fontSize: '8.5px', fontWeight: 700, color: C.gold, marginBottom: 2 }}>Low Stock Alert</div>
            <div style={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{s.sales[0].item.split(' ')[0]}, only 3 left</div>
          </div>
        </div>
      </div>

      {/* Floating notification — Sale recorded (bottom-left) */}
      <div style={{
        position: 'absolute', bottom: 85, left: -150,
        width: 178, background: 'rgba(5,22,14,0.97)',
        border: '1px solid rgba(34,197,94,0.38)', borderRadius: 12, padding: '9px 11px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        animation: 'float-in-left 600ms 1300ms ease both, float-bob 3.5s 2.2s ease-in-out infinite',
        zIndex: 2,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✓</div>
          <div>
            <div style={{ fontSize: '8.5px', fontWeight: 700, color: '#22C55E', marginBottom: 2 }}>Sale Recorded</div>
            <div style={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>GHS{s.sales[0].amt} · {s.sales[0].item.split(' ')[0]}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Scenario animated numbers ─────────────────────────────────────────────────
function ScenarioNumbers({ si }) {
  const s = SCENARIOS[si];
  const rev   = useAnimatedCounter(s.revenue,   1000);
  const cogs  = useAnimatedCounter(s.cogs,      1000);
  const gross = useAnimatedCounter(s.gross,     1000);
  const rent  = useAnimatedCounter(s.rent,      1000);
  const sal   = useAnimatedCounter(s.salaries,  1000);
  const net   = useAnimatedCounter(s.net,       1000);
  const prod  = useAnimatedCounter(s.products,  800);
  const cust  = useAnimatedCounter(s.customers, 800);

  return (
    <>
      {/* 4 metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Monthly Revenue',  val: fmtC(rev),        color: '#22C55E', trend: s.revTrend, minW: '11ch' },
          { label: 'Products Tracked', val: Math.round(prod), color: 'white',   trend: null,       minW: '3ch'  },
          { label: 'Customers',        val: Math.round(cust), color: '#60A5FA', trend: null,       minW: '3ch'  },
          { label: 'Net Profit',       val: fmtC(net),        color: C.gold,    trend: s.netTrend, minW: '11ch' },
        ].map(({ label, val, color, trend, minW }) => (
          <div key={label} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px' }}>
            <div style={{ fontSize: '0.68rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{label}</div>
            <div style={{
              fontSize: '1.35rem', fontWeight: 800, color, lineHeight: 1,
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              minWidth: minW, display: 'inline-block',
            }}>{val}</div>
            {trend && <div style={{ fontSize: '0.72rem', color: '#22C55E', marginTop: 6, fontWeight: 600 }}>↑ {trend} vs last month</div>}
          </div>
        ))}
      </div>

      {/* P&L breakdown */}
      <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 14, padding: '20px 24px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
          P&amp;L — {s.name}
        </div>
        <PLRow label="Total Revenue"      value={fmtC(rev)}   color="#22C55E" />
        <PLRow label="Cost of Goods Sold" value={fmtC(cogs)}  color="#EF4444" minus />
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '6px 0' }} />
        <PLRow label="Gross Profit"       value={fmtC(gross)} bold />
        <div style={{ height: 6 }} />
        <PLRow label="Rent & Utilities"   value={fmtC(rent)}  color="#EF4444" minus />
        <PLRow label="Salaries"           value={fmtC(sal)}   color="#EF4444" minus />
        <div style={{ borderTop: '2px solid rgba(255,255,255,0.15)', margin: '6px 0' }} />
        <PLRow label="NET PROFIT"         value={fmtC(net)}   color={C.gold}  bold large />
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [si, setSi]             = useState(0);
  const [userPicked, setUserPicked] = useState(false);

  // Contact modal
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactState, setContactState] = useState('idle'); // idle | sending | success | error
  const [contactError, setContactError] = useState('');

  const [featRef,  featInView]  = useInView(0.08);
  const [testRef,  testInView]  = useInView(0.08);
  const [chatRef,  chatInView]  = useInView(0.06);
  const [scenRef,  scenInView]  = useInView(0.06);

  // Auto-rotate scenarios
  useEffect(() => {
    if (userPicked) return;
    const id = setInterval(() => setSi((s) => (s + 1) % SCENARIOS.length), 4000);
    return () => clearInterval(id);
  }, [userPicked]);

  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  if (loading) return null;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const openContact = () => {
    setContactForm({ name: '', email: '', message: '' });
    setContactState('idle');
    setContactError('');
    setShowContact(true);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactState('sending');
    setContactError('');
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setContactState('success');
    } catch (err) {
      console.error('[Landing] contact submit error:', err);
      setContactError('Failed to send your message. Please try again.');
      setContactState('error');
    }
  };
  const sec = (bg, extra = {}) => ({ background: bg, padding: '88px 24px', ...extra });
  const navLinkBase = { background: 'none', border: 'none', color: C.muted, fontSize: '0.875rem', cursor: 'pointer', padding: '8px 14px', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, transition: 'color 150ms' };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: 'white', lineHeight: 1.6, background: C.bg }}>

      {/* ══════════════════════════════════════════════════════ 1. NAV ══ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(11,22,40,0.90)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '0 40px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        animation: 'nav-slide-down 500ms ease forwards',
      }}>
        <img src="/optika-logo-white.svg" alt="OptiKa" style={{ height: '36px', width: 'auto' }} />
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {[['Features', 'features'], ['Pricing', 'pricing']].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} style={navLinkBase}
              onMouseEnter={(e) => (e.target.style.color = C.gold)}
              onMouseLeave={(e) => (e.target.style.color = C.muted)}
            >{label}</button>
          ))}
          <Link to="/login" style={{ ...navLinkBase, textDecoration: 'none', display: 'inline-block' }}>Sign in</Link>
          <GoldBtn to="/register">Start 14-Day Trial →</GoldBtn>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════ 2. HERO ══ */}
      <section style={{
        ...sec(C.bg, { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', padding: '100px 40px 80px' }),
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -5%, rgba(212,160,23,0.13), transparent)`,
      }}>
        {/* Depth blobs */}
        {[{ w: 640, top: '40%', left: '50%', color: 'rgba(212,160,23,0.06)' }, { w: 400, top: '65%', left: '15%', color: 'rgba(27,58,107,0.1)' }, { w: 320, top: '20%', left: '80%', color: 'rgba(26,122,74,0.06)' }].map(({ w, top, left, color }, i) => (
          <div key={i} style={{ position: 'absolute', width: w, height: w, borderRadius: '50%', background: `radial-gradient(circle, ${color}, transparent 70%)`, top, left, transform: 'translate(-50%,-50%)', filter: 'blur(64px)', pointerEvents: 'none' }} />
        ))}

        {/* Two-column layout */}
        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1100, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 64, alignItems: 'center' }}>

          {/* Left — copy */}
          <div>
            {/* Badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, border: `1px solid rgba(212,160,23,0.35)`, borderRadius: 999, padding: '6px 18px', marginBottom: 28, background: 'rgba(212,160,23,0.07)', animation: 'slide-up 500ms ease forwards', animationDelay: '0ms', opacity: 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.gold, animation: 'pulse-ring 1.5s ease-in-out infinite' }} />
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: C.gold }}>✦ Built for Ghanaian Businesses</span>
            </div>

            <h1 style={{ fontSize: 'clamp(2.4rem,6vw,4.2rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, lineHeight: 1.1, marginBottom: 22, letterSpacing: '-0.03em', animation: 'slide-up 500ms ease forwards', animationDelay: '100ms', opacity: 0 }}>
              Run Your Business<br />
              Like a <span style={{ position: 'relative', color: C.gold }}>
                Pro
                <span style={{ position: 'absolute', bottom: -5, left: 0, width: '100%', height: 4, borderRadius: 2, background: `linear-gradient(90deg,${C.gold},${C.goldLight})`, display: 'block', animation: 'underline-draw 700ms 700ms ease forwards', transform: 'scaleX(0)', transformOrigin: 'left' }} />
              </span>
            </h1>

            <p style={{ fontSize: '1.05rem', color: C.muted, maxWidth: 480, marginBottom: 36, lineHeight: 1.8, animation: 'slide-up 500ms ease forwards', animationDelay: '200ms', opacity: 0 }}>
              Track inventory, manage customers, record sales and know your real profit — all in one place. In Cedis.
            </p>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 52, animation: 'slide-up 500ms ease forwards', animationDelay: '300ms', opacity: 0 }}>
              <GoldBtn to="/register" big>Start 14-Day Trial →</GoldBtn>
              <button onClick={() => scrollTo('scenarios')} style={{ background: 'transparent', color: 'white', padding: '16px 28px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.22)', fontSize: '1rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 200ms' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
              >Watch Demo</button>
            </div>
            <a href="/download" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', textDecoration: 'none', transition: 'color 200ms' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
              Download desktop app for Windows
            </a>

            {/* Trust stats */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', animation: 'slide-up 500ms ease forwards', animationDelay: '400ms', opacity: 0 }}>
              {[{ val: 'GHS', label: 'Ghana Cedis native' }, { val: '2 min', label: 'To get started' }, { val: '14 Days', label: 'Free trial included' }].map(({ val, label }, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                  {i > 0 && <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.12)', margin: '0 24px' }} />}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: C.gold, fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>{val}</div>
                    <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 3 }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — phone mockup */}
          <div style={{ display: 'flex', justifyContent: 'center', animation: 'slide-up 600ms ease forwards', animationDelay: '200ms', opacity: 0 }}>
            <PhoneMockup si={si} />
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.6rem', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase' }}>Scroll</span>
          <div style={{ width: 1, height: 40, background: `linear-gradient(to bottom, ${C.gold}, transparent)`, animation: 'scroll-pulse 2s ease-in-out infinite' }} />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ 3. LIVE SCENARIOS ══ */}
      <section id="scenarios" style={sec(C.surface)}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.gold, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Live Demo</div>
            <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.5rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, marginBottom: 14, lineHeight: 1.15 }}>See Any Business Type in Action</h2>
            <p style={{ color: C.muted, fontSize: '1rem', maxWidth: 520, margin: '0 auto' }}>
              Switch between real business scenarios and watch the numbers update live.
            </p>
          </div>

          {/* Tab buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
            {SCENARIOS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => { setSi(i); setUserPicked(true); }}
                style={{
                  padding: '10px 20px', borderRadius: 10, fontFamily: 'Sora, sans-serif',
                  fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                  transition: 'all 200ms',
                  background: si === i ? C.gold : 'transparent',
                  color: si === i ? '#0F172A' : C.muted,
                  border: si === i ? 'none' : `1px solid ${C.border}`,
                  boxShadow: si === i ? '0 4px 16px rgba(212,160,23,0.3)' : 'none',
                }}
                onMouseEnter={(e) => { if (si !== i) { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; } }}
                onMouseLeave={(e) => { if (si !== i) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; } }}
              >
                {s.tab}
              </button>
            ))}
          </div>

          {/* Auto-rotate indicator */}
          {!userPicked && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.gold, animation: 'pulse-ring 1.5s ease-in-out infinite' }} />
                <span style={{ fontSize: '0.75rem', color: C.muted }}>Auto-switching every 4s · Click a tab to stop</span>
              </div>
            </div>
          )}

          {/* Scenario content card */}
          <div
            ref={scenRef}
            style={{
              background: 'rgba(11,22,40,0.6)', border: `1px solid ${C.border}`, borderRadius: 20, padding: '28px 28px',
              transition: 'opacity 500ms, transform 500ms',
              opacity: scenInView ? 1 : 0, transform: scenInView ? 'translateY(0)' : 'translateY(24px)',
              contain: 'layout',
              overflow: 'hidden',
            }}
          >
            <ScenarioNumbers si={si} />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════ 4. TESTIMONIALS ══ */}
      <section style={sec(C.bg)}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.gold, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Real People. Real Results.</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3.5vw,2.3rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800 }}>Business Owners Love OptiKa</h2>
          </div>

          <div ref={testRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {TESTIMONIALS.map(({ quote, name, role, color, initials }, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.gold}`,
                  borderRadius: 16, padding: 32,
                  transition: `opacity 500ms ${i * 100}ms, transform 500ms ${i * 100}ms cubic-bezier(0.4,0,0.2,1)`,
                  opacity: testInView ? 1 : 0, transform: testInView ? 'translateY(0)' : 'translateY(24px)',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderLeftColor = C.goldLight; e.currentTarget.style.boxShadow = '0 8px 32px rgba(212,160,23,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = testInView ? 'translateY(0)' : 'translateY(24px)'; e.currentTarget.style.borderLeftColor = C.gold; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ color: C.gold, fontSize: '0.82rem', letterSpacing: 1, marginBottom: 16 }}>★★★★★</div>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.93rem', lineHeight: 1.75, marginBottom: 24, fontStyle: 'italic' }}>{quote}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${color}99)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800, color: 'white', fontFamily: 'Sora, sans-serif', flexShrink: 0 }}>
                    {initials}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{name}</div>
                    <div style={{ fontSize: '0.78rem', color: C.muted }}>{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ 5. CHAT CONVERSATIONS ══ */}
      <section style={sec(C.surface)}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.gold, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>From Our Users</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3.5vw,2.2rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, marginBottom: 12 }}>Real Conversations From Our Users</h2>
            <p style={{ color: C.muted, fontSize: '1rem' }}>See how OptiKa helps business owners every day.</p>
          </div>

          <div ref={chatRef} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {CHAT.map(({ type, av, color, name, time, text }, i) => {
              const isUser = type === 'user';
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', gap: 12, alignItems: 'flex-start',
                    transition: `opacity 500ms ${i * 300}ms, transform 500ms ${i * 300}ms cubic-bezier(0.4,0,0.2,1)`,
                    opacity: chatInView ? 1 : 0, transform: chatInView ? 'translateY(0)' : 'translateY(20px)',
                  }}
                >
                  {/* Avatar */}
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${color}bb)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, color: 'white', fontFamily: 'Sora, sans-serif', flexShrink: 0, marginTop: 2 }}>
                    {av}
                  </div>
                  <div style={{ maxWidth: '76%', display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                    <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 5, display: 'flex', gap: 8 }}>
                      <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{name}</span>
                      <span>{time}</span>
                    </div>
                    <div style={{
                      padding: '12px 16px', borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                      fontSize: '0.9rem', lineHeight: 1.65,
                      background: isUser ? 'rgba(212,160,23,0.12)' : 'rgba(255,255,255,0.06)',
                      border: isUser ? `1px solid rgba(212,160,23,0.25)` : `1px solid ${C.border}`,
                      color: 'rgba(255,255,255,0.85)',
                    }}>
                      {text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═════════════════════════════════════════════════ 6. FEATURES ══ */}
      <section id="features" style={sec(C.bg)}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.gold, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Everything You Need</div>
            <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.5rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, marginBottom: 14, lineHeight: 1.15 }}>One Tool. Total Control.</h2>
            <p style={{ color: C.muted, fontSize: '1rem', maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>Stop using paper, WhatsApp and spreadsheets separately. OptiKa brings it all together.</p>
          </div>

          <div ref={featRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {FEATURES.map(({ icon, title, desc }, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
                  borderRadius: 16, padding: 32, cursor: 'default',
                  transition: `opacity 500ms ${i * 80}ms, transform 500ms ${i * 80}ms cubic-bezier(0.4,0,0.2,1)`,
                  opacity: featInView ? 1 : 0, transform: featInView ? 'translateY(0)' : 'translateY(28px)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(212,160,23,0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = featInView ? 'translateY(0)' : 'translateY(28px)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(212,160,23,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: 20 }}>{icon}</div>
                <h3 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1rem', marginBottom: 10 }}>{title}</h3>
                <p style={{ color: C.muted, fontSize: '0.875rem', lineHeight: 1.7, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ 7. PRICING ══ */}
      <section id="pricing" style={sec(C.surface)}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.gold, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Simple Pricing</div>
            <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.5rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, marginBottom: 14, lineHeight: 1.15 }}>
              Start Free. Upgrade When Ready.
            </h2>
            <p style={{ color: C.muted, fontSize: '1rem', maxWidth: 500, margin: '0 auto', lineHeight: 1.75 }}>
              Every account starts with a 14-day free trial. No card required. Pay in Cedis via MoMo or card.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, maxWidth: 1000, margin: '0 auto' }}>
            {/* Basic */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 20, padding: 32, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '3px 12px', borderRadius: 20, display: 'inline-block', marginBottom: 20, alignSelf: 'flex-start' }}>BASIC</div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: C.muted, lineHeight: 1 }}>GHS</span>
                  <span style={{ fontSize: '2.8rem', fontWeight: 800, color: 'white', fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>200</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: C.muted, marginTop: 6 }}>per month · 2 users · 1 store</div>
              </div>
              <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, flex: 1 }}>
                {['2 users (owner + 1 staff)', '1 store / branch', 'Point of Sale (POS)', 'Sales & Sales Orders', 'Inventory & Purchase Orders', 'Customer Management & Invoicing', 'Debt Tracking', 'Business Dashboard', 'Sales & Inventory Reports'].map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>
                    <span style={{ color: '#60A5FA', fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <GoldBtn to="/register" style={{ textAlign: 'center', display: 'block' }}>Get Started →</GoldBtn>
            </div>

            {/* Growth — highlighted */}
            <div style={{ background: 'linear-gradient(145deg, rgba(212,160,23,0.1), rgba(212,160,23,0.04))', border: `2px solid rgba(212,160,23,0.45)`, borderRadius: 20, padding: 32, display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 0 40px rgba(212,160,23,0.12)' }}>
              <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: C.gold, color: '#0F172A', fontSize: '0.7rem', fontWeight: 800, padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>MOST POPULAR</div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.gold, background: 'rgba(212,160,23,0.12)', padding: '3px 12px', borderRadius: 20, display: 'inline-block', marginBottom: 20, alignSelf: 'flex-start' }}>GROWTH</div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: C.muted, lineHeight: 1 }}>GHS</span>
                  <span style={{ fontSize: '2.8rem', fontWeight: 800, color: 'white', fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>400</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: C.muted, marginTop: 6 }}>per month · 5 users · 1 store</div>
              </div>
              <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, flex: 1 }}>
                {['5 users (owner + 4 staff)', '1 store / branch', 'Everything in Basic', 'CRM & Lead Management', 'Accounts Receivable / Payable Aging', 'Staff & Payroll Management', 'Financial Management', 'Business Analytics', 'Financial Intelligence Reports', 'Month-End Close'].map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>
                    <span style={{ color: C.gold, fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <GoldBtn to="/register" style={{ textAlign: 'center', display: 'block' }}>Get Started →</GoldBtn>
            </div>

            {/* Enterprise */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 20, padding: 32, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#F59E0B', background: 'rgba(245,158,11,0.12)', padding: '3px 12px', borderRadius: 20, display: 'inline-block', marginBottom: 20, alignSelf: 'flex-start' }}>ENTERPRISE</div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: C.muted, lineHeight: 1 }}>GHS</span>
                  <span style={{ fontSize: '2.8rem', fontWeight: 800, color: 'white', fontFamily: 'Sora, sans-serif', lineHeight: 1 }}>800</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: C.muted, marginTop: 6 }}>per month · unlimited users & branches</div>
              </div>
              <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, flex: 1 }}>
                {['Unlimited users', 'Unlimited branches / stores', 'Everything in Growth', 'AI-Powered Business Insights', 'Inventory Intelligence', 'Security Audit Log', 'Multi-Branch Reporting', 'Priority Support', 'API Access'].map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>
                    <span style={{ color: '#F59E0B', fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <GoldBtn to="/register" style={{ textAlign: 'center', display: 'block' }}>Get Started →</GoldBtn>
            </div>
          </div>

          {/* Payment methods */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 36, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: C.muted }}>Accepted payments:</span>
            {[{ icon: '📱', label: 'MTN MoMo' }, { icon: '📱', label: 'Vodafone Cash' }, { icon: '💳', label: 'Visa / Mastercard' }].map(({ icon, label }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '4px 12px' }}>
                {icon} {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════ 8. FINAL CTA ══ */}
      <section style={{ ...sec(`linear-gradient(135deg, ${C.surface} 0%, ${C.navy} 100%)`), textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontFamily: 'Sora, sans-serif', fontWeight: 800, lineHeight: 1.15, marginBottom: 20 }}>
            Your Business Deserves{' '}<span style={{ color: C.gold }}>Better Tools</span>
          </h2>
          <p style={{ color: C.muted, fontSize: '1.05rem', lineHeight: 1.75, marginBottom: 40 }}>
            Join business owners across Ghana who track smarter, sell more, and stress less.
          </p>
          <GoldBtn to="/register" big>Create Trial Account →</GoldBtn>
          <div style={{ marginTop: 16, fontSize: '0.82rem', color: C.muted }}>
            14-day free trial · No card required to start
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════ CONTACT MODAL ══ */}
      {showContact && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowContact(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div style={{ background: '#0D1E38', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '36px 40px', width: '100%', maxWidth: 460, position: 'relative' }}>
            <button onClick={() => setShowContact(false)} style={{ position: 'absolute', top: 16, right: 18, background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1, padding: 4 }}>✕</button>

            {contactState === 'success' ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✉️</div>
                <div style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: 'white', marginBottom: 10 }}>Message sent!</div>
                <div style={{ color: C.muted, fontSize: '0.9rem', lineHeight: 1.7 }}>We'll get back to you as soon as possible.</div>
                <button onClick={() => setShowContact(false)} style={{ marginTop: 28, background: C.gold, color: '#0F172A', border: 'none', borderRadius: 10, padding: '10px 28px', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Sora, sans-serif' }}>Done</button>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.15rem', fontWeight: 800, color: 'white', marginBottom: 6 }}>Get in Touch</div>
                <div style={{ color: C.muted, fontSize: '0.85rem', marginBottom: 28 }}>Send us a message and we'll respond quickly.</div>

                <form onSubmit={submitContact} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Name</label>
                    <input
                      type="text" required placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'white', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                      onFocus={(e) => (e.target.style.borderColor = C.gold)}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Email</label>
                    <input
                      type="email" required placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'white', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                      onFocus={(e) => (e.target.style.borderColor = C.gold)}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Message</label>
                    <textarea
                      required rows={5} placeholder="How can we help?"
                      value={contactForm.message}
                      onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'white', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                      onFocus={(e) => (e.target.style.borderColor = C.gold)}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                  </div>

                  {contactState === 'error' && (
                    <div style={{ fontSize: '0.82rem', color: '#FF6B6B', background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 8, padding: '10px 14px' }}>
                      {contactError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={contactState === 'sending'}
                    style={{ background: C.gold, color: '#0F172A', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 800, fontSize: '0.9rem', cursor: contactState === 'sending' ? 'wait' : 'pointer', fontFamily: 'Sora, sans-serif', opacity: contactState === 'sending' ? 0.7 : 1, transition: 'opacity 150ms' }}
                  >
                    {contactState === 'sending' ? 'Sending…' : 'Send Message →'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════ 9. FOOTER ══ */}
      <footer style={{ background: C.bg, borderTop: `1px solid ${C.border}`, padding: '36px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
        <img src="/optika-logo-white.svg" alt="OptiKa" style={{ height: '28px', width: 'auto' }} />
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['Features', 'features'], ['Pricing', 'pricing']].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit', padding: 0, transition: 'color 150ms' }}
              onMouseEnter={(e) => (e.target.style.color = C.gold)} onMouseLeave={(e) => (e.target.style.color = C.muted)}
            >{label}</button>
          ))}
          <button onClick={openContact} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit', padding: 0, transition: 'color 150ms' }}
            onMouseEnter={(e) => (e.target.style.color = C.gold)} onMouseLeave={(e) => (e.target.style.color = C.muted)}
          >Contact</button>
          <Link to="/privacy" style={{ color: C.muted, textDecoration: 'none', fontSize: '0.875rem', transition: 'color 150ms' }}
            onMouseEnter={(e) => (e.target.style.color = C.gold)} onMouseLeave={(e) => (e.target.style.color = C.muted)}
          >Privacy</Link>
        </div>
        <div style={{ color: C.muted, fontSize: '0.78rem' }}>© 2026 OptiKa · Built for Ghana 🇬🇭</div>
      </footer>
    </div>
  );
}
